import type { LucideIcon } from 'lucide-react';

export type StatusTone = 'success' | 'warning' | 'info' | 'danger' | 'neutral' | 'accent';

export interface StatusBadgeConfig {
  label: string;
  className: string;
  dotClassName: string;
  icon?: LucideIcon;
}

const TONE_CLASSES: Record<StatusTone, { className: string; dotClassName: string }> = {
  success: {
    className: 'bg-green-100 text-green-800 border-green-200',
    dotClassName: 'bg-green-500',
  },
  warning: {
    className: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    dotClassName: 'bg-yellow-500',
  },
  info: {
    className: 'bg-blue-100 text-blue-800 border-blue-200',
    dotClassName: 'bg-blue-500',
  },
  danger: {
    className: 'bg-red-100 text-red-800 border-red-200',
    dotClassName: 'bg-red-500',
  },
  neutral: {
    className: 'bg-gray-100 text-gray-800 border-gray-200',
    dotClassName: 'bg-gray-500',
  },
  accent: {
    className: 'bg-purple-100 text-purple-800 border-purple-200',
    dotClassName: 'bg-purple-500',
  },
};

const createStatusConfig = (label: string, tone: StatusTone, icon?: LucideIcon): StatusBadgeConfig => ({
  label,
  className: TONE_CLASSES[tone].className,
  dotClassName: TONE_CLASSES[tone].dotClassName,
  icon,
});

const STATUS_BADGE_CONFIG: Record<string, StatusBadgeConfig> = {
  draft: createStatusConfig('Draft', 'warning'),
  active: createStatusConfig('Active', 'info'),
  completed: createStatusConfig('Completed', 'success'),
  paused: createStatusConfig('Paused', 'neutral'),
  archived: createStatusConfig('Archived', 'neutral'),
  pending: createStatusConfig('Pending', 'warning'),
  paid: createStatusConfig('Paid', 'success'),
  overdue: createStatusConfig('Overdue', 'danger'),
  partial: createStatusConfig('Partial', 'info'),
  'partially-paid': createStatusConfig('Partially Paid', 'info'),
  processing: createStatusConfig('Processing', 'info'),
  submitted: createStatusConfig('Submitted', 'info'),
  under_review: createStatusConfig('Under Review', 'warning'),
  approved: createStatusConfig('Approved', 'success'),
  rejected: createStatusConfig('Rejected', 'danger'),
  published: createStatusConfig('Published', 'accent'),
  verified: createStatusConfig('Verified', 'success'),
  failed: createStatusConfig('Failed', 'danger'),
  open: createStatusConfig('Open', 'warning'),
  in_progress: createStatusConfig('In Progress', 'info'),
  invited: createStatusConfig('Invited', 'info'),
  accepted: createStatusConfig('Accepted', 'success'),
  cancelled: createStatusConfig('Cancelled', 'danger'),
  declined: createStatusConfig('Declined', 'danger'),
  confirmed: createStatusConfig('Confirmed', 'success'),
  suggested: createStatusConfig('Suggested', 'neutral'),
  contacted: createStatusConfig('Contacted', 'info'),
  'awaiting approval': createStatusConfig('Awaiting Approval', 'info'),
  awaiting_approval: createStatusConfig('Awaiting Approval', 'info'),
  'payment in pipeline': createStatusConfig('Payment in Pipeline', 'warning'),
  payment_in_pipeline: createStatusConfig('Payment in Pipeline', 'warning'),
  'content in progress': createStatusConfig('Content In Progress', 'info'),
  content_in_progress: createStatusConfig('Content In Progress', 'info'),
  'content shared': createStatusConfig('Content Shared', 'info'),
  content_shared: createStatusConfig('Content Shared', 'info'),
  'content approved': createStatusConfig('Content Approved', 'success'),
  content_approved: createStatusConfig('Content Approved', 'success'),
  'content rejected': createStatusConfig('Content Rejected', 'danger'),
  content_rejected: createStatusConfig('Content Rejected', 'danger'),
  delivered: createStatusConfig('Delivered', 'success'),
  'pending approval': createStatusConfig('Pending Approval', 'warning'),
  'revision needed': createStatusConfig('Revision Needed', 'danger'),
  'in negotiation': createStatusConfig('In Negotiation', 'warning'),
  suspended: createStatusConfig('Suspended', 'danger'),
  inactive: createStatusConfig('Inactive', 'neutral'),
  unknown: createStatusConfig('Unknown', 'neutral'),
};

export const normalizeStatusKey = (status: string): string =>
  status.trim().toLowerCase().replace(/[\s-]+/g, '_');

const getStatusLookupKeys = (status: string): string[] => {
  const lower = status.trim().toLowerCase();
  return Array.from(
    new Set([
      lower,
      lower.replace(/[\s-]+/g, '_'),
      lower.replace(/[_-]+/g, ' '),
    ])
  );
};

export const formatStatusLabel = (status: string): string => {
  const compact = status.trim().replace(/[_-]+/g, ' ').replace(/\s+/g, ' ');

  return compact
    .split(' ')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

export const getStatusBadgeConfig = (status: string | null | undefined, label?: string): StatusBadgeConfig => {
  if (!status) {
    return createStatusConfig(label || 'Unknown', 'neutral');
  }

  for (const key of getStatusLookupKeys(status)) {
    const config = STATUS_BADGE_CONFIG[key];
    if (config) {
      return {
        ...config,
        label: label || config.label,
      };
    }
  }

  const fallbackLabel = label || formatStatusLabel(status);
  return createStatusConfig(fallbackLabel, 'neutral');
};

export const getStatusBadgeClassName = (status: string | null | undefined): string =>
  getStatusBadgeConfig(status).className;

export const getStatusBadgeLabel = (status: string | null | undefined): string =>
  getStatusBadgeConfig(status).label;

export const getStatusBadgeDotClassName = (status: string | null | undefined): string =>
  getStatusBadgeConfig(status).dotClassName;
